import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';

/**
 * ADR-0013: the app is served from `/app`, so every route the router matches
 * and every URL it writes carries that prefix.
 *
 * Taken from `import.meta.env.BASE_URL`, which vite fills from the `base` in
 * `vite.config.ts`, so the two cannot drift. React Router wants a basename
 * without a trailing slash; vite always supplies one.
 */
const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, '');

import { DialogProvider, ErrorBoundary, ToastProvider } from '@good-season/ui';

import { HelpMount } from './help/HelpMount.js';
import { UpdatePrompt } from './pwa/UpdatePrompt.js';
import { Landing } from './routes/Landing.js';
import { LazyScreen, RoutePlaceholder, loadChunk } from './routes/LazyScreen.js';
import { SignIn } from './routes/SignIn.js';
import { ScorebookScreen } from './scorebook/ScorebookScreen.js';
import { ThemeProvider } from './theme/ThemeProvider.js';

/*
 * Loaded when opened, not in the first download (ADR-0096). The scorebook
 * screens are NOT here: they must open with no signal.
 */
/** Setup needs the network once and the box score is a report: neither is the live book. Both are precached. */
const GameSetupScreen = lazy(() =>
  loadChunk(() => import('./scorebook/GameSetupScreen.js')).then((m) => ({
    default: m.GameSetupScreen,
  })),
);
const BoxScoreScreen = lazy(() =>
  loadChunk(() => import('./scorebook/BoxScoreScreen.js')).then((m) => ({
    default: m.BoxScoreScreen,
  })),
);
const FamilySignupScreen = lazy(() =>
  loadChunk(() => import('./routes/family-signup/FamilySignupScreen.js')),
);
const RsvpScreen = lazy(() =>
  loadChunk(() => import('./routes/rsvp/RsvpScreen.js')).then((m) => ({
    default: m.RsvpScreen,
  })),
);
const EditName = lazy(() =>
  loadChunk(() => import('./routes/EditName.js')).then((m) => ({ default: m.EditName })),
);
const AdminScreen = lazy(() =>
  loadChunk(() => import('./routes/admin/AdminScreen.js')).then((m) => ({
    default: m.AdminScreen,
  })),
);
const TeamMembersScreen = lazy(() =>
  loadChunk(() => import('./routes/admin/TeamMembersScreen.js')).then((m) => ({
    default: m.TeamMembersScreen,
  })),
);
const SupportTicketsScreen = lazy(() =>
  loadChunk(() => import('./routes/admin/SupportTicketsScreen.js')).then((m) => ({
    default: m.SupportTicketsScreen,
  })),
);
const ClubInviteScreen = lazy(() =>
  loadChunk(() => import('./routes/club/ClubInviteScreen.js')).then((m) => ({
    default: m.ClubInviteScreen,
  })),
);
const NotFound = lazy(() =>
  loadChunk(() => import('./routes/NotFound.js')).then((m) => ({
    default: m.NotFound,
  })),
);
const TeamFlyerScreen = lazy(() =>
  loadChunk(() => import('./routes/flyer/TeamFlyerScreen.js')).then((m) => ({
    default: m.TeamFlyerScreen,
  })),
);
const OnboardingWizard = lazy(() =>
  loadChunk(() => import('./routes/onboarding/OnboardingWizard.js')).then((m) => ({
    default: m.OnboardingWizard,
  })),
);
/*
 * Lazy like `/welcome`: the household screen reuses the onboarding wizard's
 * steps, so an eager import here would pull the wizard into the first
 * download too (ADR-0096).
 */
const HouseholdScreen = lazy(() =>
  loadChunk(() => import('./routes/household/HouseholdScreen.js')).then((m) => ({
    default: m.HouseholdScreen,
  })),
);
const TeamInviteWizard = lazy(() =>
  loadChunk(() => import('./routes/team-invite/TeamInviteWizard.js')).then((m) => ({
    default: m.TeamInviteWizard,
  })),
);

const AllergyCardScreen = lazy(() =>
  loadChunk(() => import('./routes/allergy/AllergyCardScreen.js')).then((m) => ({
    default: m.AllergyCardScreen,
  })),
);
const ScheduleScreen = lazy(() =>
  loadChunk(() => import('./routes/ScheduleScreen.js')).then((m) => ({
    default: m.ScheduleScreen,
  })),
);
const ChangePassword = lazy(() =>
  loadChunk(() => import('./routes/ChangePassword.js')).then((m) => ({
    default: m.ChangePassword,
  })),
);
const TeamScreen = lazy(() =>
  loadChunk(() => import('./routes/TeamScreen.js')).then((m) => ({ default: m.TeamScreen })),
);
const LeagueScreen = lazy(() =>
  loadChunk(() => import('./routes/league/LeagueScreen.js')).then((m) => ({
    default: m.LeagueScreen,
  })),
);
const InvitationsScreen = lazy(() =>
  loadChunk(() => import('./routes/invitations/InvitationsScreen.js')).then((m) => ({
    default: m.InvitationsScreen,
  })),
);
const ChangelogScreen = lazy(() =>
  loadChunk(() => import('./routes/ChangelogScreen.js')).then((m) => ({
    default: m.ChangelogScreen,
  })),
);
const AnnouncementsScreen = lazy(() =>
  loadChunk(() => import('./routes/announcements/AnnouncementsScreen.js')).then((m) => ({
    default: m.AnnouncementsScreen,
  })),
);

/**
 * Push and email links sent before ADR-0122 pointed a question at
 * `/team?team=...&thread=...`. Those links are in inboxes; they open the
 * announcements screen now, with every parameter carried over.
 */
function LegacyThreadLink({ children }: { readonly children: React.ReactNode }) {
  const [params] = useSearchParams();
  if (params.get('thread') === null) return children;
  return <Navigate replace to={{ pathname: '/announcements', search: `?${params.toString()}` }} />;
}

/**
 * Layout route for the three scorebook screens: a render error inside one of
 * them lands on the themed fallback instead of unmounting the whole app.
 *
 * What stays on screen is what `App` renders outside the routes (the providers
 * and `UpdatePrompt`). There is no navigation shell out there: `AppShell` is
 * rendered by each screen, and the scorebook screens deliberately do not use it.
 * So what this buys is containment: the root boundary in `main.tsx` would also
 * catch the error, but it would replace the providers and the update bar too.
 *
 * `ErrorBoundary` has no reset key, so it is keyed by pathname: a navigation
 * to another scorebook route mounts a fresh boundary. The fallback offers
 * "Back to the schedule", which navigates inside the router to `/schedule`,
 * keeping `search` so `?team=` survives (outside this layout), because
 * "Try again" only reloads the same URL and would not help a crash that
 * repeats on every render. `SB` is the scorebook's diagnostic prefix.
 */
function ScorebookBoundary() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  return (
    <ErrorBoundary
      key={pathname}
      surface="SB"
      fills="screen"
      secondaryAction={{
        label: 'Back to the schedule',
        onSelect: () => {
          // Carries `search`: the selected team lives only in `?team=`.
          void navigate({ pathname: '/schedule', search });
        },
      }}
    >
      <Suspense fallback={<RoutePlaceholder chrome="bare" />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * The routed screens, keyed by the team `?team=` currently names.
 *
 * `useTeams().selectTeam()` (`apps/web/src/team/useTeams.ts`) changes that
 * parameter with `setSearchParams()`, which react-router treats as a plain
 * navigation: it does not remount whichever screen is already showing, so
 * `TeamScreen` and `ScheduleScreen`'s existing `useEffect(() => {...}, [])`
 * fetch would never run again and the screen would keep showing the team it
 * had before the switch, with the URL now silently disagreeing with it.
 *
 * Keyed here rather than adding `search` to each screen's own effect
 * dependencies, which would need remembering on every future screen this
 * shell wraps. `key` changing is what React already treats as "this is a
 * different element": on a real team switch it unmounts the matched screen
 * and mounts a fresh one, running its on-mount fetch again against the newly
 * selected team. Navigating between screens WITHOUT switching teams is
 * unaffected, since the key does not change and react-router swaps the
 * matched route the same way it always has.
 */
function AppRoutes() {
  const [searchParams] = useSearchParams();
  const team = searchParams.get('team') ?? '';

  return (
    <Routes key={team}>
      {/*
        The root decides where a signed-in person belongs, per the
        owner's decision on 7 August 2026: staff to their team,
        guardians to Parent Home, nobody to `/signin`.

        It used to render `ParentHome` unconditionally, so a head coach
        who typed the bare domain got a static mock of a fictional
        family's snack duty presented as their app. `ParentHome` is no
        longer routed directly; `Landing` renders it for the role it
        was designed for.
      */}
      <Route path="/" element={<Landing />} />
      <Route
        path="/account/name"
        element={
          <LazyScreen chrome="shell">
            <EditName />
          </LazyScreen>
        }
      />
      <Route path="/signin" element={<SignIn />} />
      <Route
        path="/join"
        element={
          <LazyScreen chrome="bare">
            <FamilySignupScreen />
          </LazyScreen>
        }
      />
      {/*
        Change password (issue #103, ADR-0101). The card frame, like sign-in,
        because it is reached straight from a magic link by somebody who
        forgot their password. Added to `e2e/accessibility.spec.ts` in the
        same commit.
      */}
      <Route
        path="/account/password"
        element={
          <LazyScreen chrome="bare">
            <ChangePassword />
          </LazyScreen>
        }
      />
      {/*
        The guardian onboarding wizard (BUILD_PLAN 4.7). An invitation lands
        here, and so does a guardian who opens the root with it unfinished.
        Outside the shell on purpose: a person half way through setting up
        has nowhere else to go yet, and the card frame is the sign-in's.

        Added to `e2e/accessibility.spec.ts` in the same commit, per that
        file's header.
      */}
      <Route
        path="/welcome"
        element={
          <LazyScreen chrome="bare">
            <OnboardingWizard />
          </LazyScreen>
        }
      />
      {/*
        A second team, by invitation (ADR-0073). Outside the shell, like
        `/welcome`: the invitee has no membership yet, sometimes no account
        at all, so there is nowhere else for them to land.

        Added to `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/join-team"
        element={
          <LazyScreen chrome="bare">
            <TeamInviteWizard />
          </LazyScreen>
        }
      />
      {/*
        Staff invitations waiting on the signed-in person (ADR-0097). In the
        shell, unlike `/join-team`: this person is signed in, and may already
        coach another team they can go back to.

        Added to `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/invitations"
        element={
          <LazyScreen chrome="shell">
            <InvitationsScreen />
          </LazyScreen>
        }
      />
      {/*
        What's new: the change log, for any signed-in role. Reached from the
        account menu and from the update banner. Added to
        `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/changelog"
        element={
          <LazyScreen chrome="shell">
            <ChangelogScreen />
          </LazyScreen>
        }
      />
      {/*
        The allergy card (BUILD_PLAN 4.7), for the coaches today and the
        meal family once meal rotation exists. Reached from the team screen.

        Added to `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/allergy-card"
        element={
          <LazyScreen chrome="shell">
            <AllergyCardScreen />
          </LazyScreen>
        }
      />
      {/*
        My household (issue #102): a guardian's allergies, emergency contact
        and photo consent after onboarding. Reached from Parent Home.

        Added to `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/household"
        element={
          <LazyScreen chrome="shell">
            <HouseholdScreen />
          </LazyScreen>
        }
      />
      {/*
        The one-page parent flyer a coach prints, reached from the team
        screen. Added to `e2e/accessibility.spec.ts` in the same commit.
      */}
      <Route
        path="/team/flyer"
        element={
          <LazyScreen chrome="shell">
            <TeamFlyerScreen />
          </LazyScreen>
        }
      />
      {/*
        The first screen behind a session. It exists to prove the path
        from a cookie to a tenant-scoped query to a name on a phone,
        which is the difference between describing the product and
        handing somebody a URL. It is now also where a coach builds the
        roster, which is the first thing they actually came to do.
      */}
      <Route
        path="/team"
        element={
          <LegacyThreadLink>
            <LazyScreen chrome="shell">
              <TeamScreen />
            </LazyScreen>
          </LegacyThreadLink>
        }
      />
      {/*
        Leagues (issue #150). Signed out, this redirects to sign-in the way
        `/team` does, so it is named beside `/team` in the accessibility
        spec rather than scanned as a second copy of the sign-in screen.
      */}
      <Route
        path="/leagues"
        element={
          <LazyScreen chrome="shell">
            <LeagueScreen />
          </LazyScreen>
        }
      />
      {/*
        The schedule. BUILD_PLAN 4.5, and the reason `AppShell` no
        longer renders this destination as a dimmed span: the screen
        exists now, so the menu item is a link.

        Added to `e2e/accessibility.spec.ts` in the same commit, per
        that file's header. A route that is not in that list is a gate
        reporting coverage it does not have.
      */}
      <Route
        path="/schedule"
        element={
          <LazyScreen chrome="shell">
            <ScheduleScreen />
          </LazyScreen>
        }
      />
      {/*
        Announcements, questions and the notification settings (ADR-0122): the
        fourth destination for every member role, with the unread badge on the
        shell. Added to `e2e/accessibility.spec.ts` in the same commit, per
        that file's header; the populated feed is scanned in a browser by
        `e2e/announcements.spec.ts` against a stubbed API.
      */}
      <Route
        path="/announcements"
        element={
          <LazyScreen chrome="shell">
            <AnnouncementsScreen />
          </LazyScreen>
        }
      />
      {/*
        Who is coming: the coach's RSVP roll-up (BUILD_PLAN 4.6). Reached
        from the schedule, where the question belongs, rather than from the
        shell's nav: D19 keeps the parent app at three destinations, and a
        guardian's own answers live on the schedule rows.

        Added to `e2e/accessibility.spec.ts` in the same commit, per that
        file's header.
      */}
      <Route
        path="/rsvp"
        element={
          <LazyScreen chrome="shell">
            <RsvpScreen />
          </LazyScreen>
        }
      />
      {/*
        The admin dashboard: every team you hold standing on, pending
        invites across all three flows, and the week ahead. Owner, head coach
        and assistant coach only (`ADMIN_DASHBOARD_ACCESS`, ADR-0108);
        `AppShell` only links here for those three roles, and the
        API route refuses anybody else the same way `/rsvp` already does.

        Added to `e2e/accessibility.spec.ts` in the same commit, per that
        file's header.
      */}
      <Route
        path="/admin"
        element={
          <LazyScreen chrome="shell">
            <AdminScreen />
          </LazyScreen>
        }
      />
      {/*
        Members and families for the team in ?team=: team code, families
        waiting to join, and the adults. Same roles as the admin dashboard.
        Added to e2e/accessibility.spec.ts in the same commit.
      */}
      <Route
        path="/admin/members"
        element={
          <LazyScreen chrome="shell">
            <TeamMembersScreen />
          </LazyScreen>
        }
      />
      {/* Help reports for platform operators. The API route is the guard (PLATFORM_ADMIN_EMAILS). */}
      <Route
        path="/admin/tickets"
        element={
          <LazyScreen chrome="shell">
            <SupportTicketsScreen />
          </LazyScreen>
        }
      />
      {/*
        Accepting a club role from the emailed link (ADR-0134). Outside the
        shell: the person may have no account yet, so there is nowhere else
        to land until they accept.

        Added to e2e/accessibility.spec.ts in the same commit.
      */}
      <Route
        path="/club/invite"
        element={
          <LazyScreen chrome="bare">
            <ClubInviteScreen />
          </LazyScreen>
        }
      />
      {/*
        The scorebook. Setup is a separate route from the book itself because
        the two have opposite requirements: setup needs the network once and
        may scroll, and the book must open from cache with none and must not.

        The book is keyed by GAME id and setup by the SCHEDULE event's, which
        is the same split the API makes: a caller naming a fixture is asking
        whether a game exists, and a caller naming a game is asserting it does.
      */}
      <Route element={<ScorebookBoundary />}>
        <Route path="/scorebook/setup/:eventId" element={<GameSetupScreen />} />
        <Route path="/scorebook/:gameId" element={<ScorebookScreen />} />
        {/*
          The box score: a report over the same game, not part of the live
          book's fixed-height screen. A separate route rather than a panel
          inside `ScorebookScreen` so it can scroll and so a coach can reach it
          from a laptop with no local copy of the game at all (see
          `BoxScoreScreen.tsx`'s own header for the two read paths).
        */}
        <Route path="/scorebook/:gameId/boxscore" element={<BoxScoreScreen />} />
      </Route>
      <Route
        path="*"
        element={
          <LazyScreen chrome="shell">
            <NotFound />
          </LazyScreen>
        }
      />
    </Routes>
  );
}

/**
 * D19: the parent app has three destinations, not nine. Home, Schedule, Team.
 * Meals, carpool, messages, and stats are not top-level; they surface on Home
 * when they are relevant and are otherwise out of the way.
 *
 * Only Home exists so far. The others land with their features rather than as
 * empty shells, because an empty shell is the "every state is designed" rule
 * being broken in advance.
 */
export function App() {
  // No theme at the root (ADR-0094): until a screen loads a team that chose
  // colors, the Good Season navy defaults in tokens.css apply. The screens that
  // load a team (Team, Schedule, RSVP) wrap themselves in that team's theme.
  // A sample palette here used to paint every team red and blue whatever it
  // had chosen, and hid the brand defaults entirely.
  const theme = null;

  return (
    /*
     * `DialogProvider` and `ToastProvider` at the root, above the router.
     *
     * Above it rather than inside a screen, because both hold a queue and a
     * provider that unmounts on navigation drops whatever was in it. A coach
     * who taps Remove and navigates away should not be able to leave a
     * confirmation promise hanging forever.
     *
     * They are here at all because `CLAUDE.md` bans `alert`, `confirm` and
     * `prompt` and CI enforces it: the moment a screen needs to ask a question,
     * this is where the answer comes from, and `useDialog()` throws with a clear
     * message rather than rendering nothing if it is missing.
     */
    <ThemeProvider theme={theme}>
      <DialogProvider>
        <ToastProvider>
          <BrowserRouter basename={BASENAME}>
            {/*
              Outside the routes, so the offer survives navigation. A person
              who changes screens while deciding should not lose the prompt,
              and the update is a property of the app rather than of any one
              route. Inside the router, because it links to What's new.
            */}
            <UpdatePrompt />
            <HelpMount />
            <AppRoutes />
          </BrowserRouter>
        </ToastProvider>
      </DialogProvider>
    </ThemeProvider>
  );
}
